import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, CloudDownload, CloudUpload, Download, RefreshCw, Trash2,
  Clock, Play, Archive, Loader2, Lock,
} from "lucide-react";
import { backupsApi, autoBackupApi } from "@/services/api";
import { apiError } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const COLLECTION_LABELS = {
  companies: "Company",
  clients: "Clients",
  contractors: "Contractors",
  buildings: "Buildings",
  doors: "Doors",
  pricing: "Pricing",
  inspections: "Inspections",
  quotes: "Quotes",
  invoices: "Invoices",
  maintenance_jobs: "Maintenance jobs",
};

const HOUR_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 22, 23];
const RETENTION_OPTIONS = [7, 14, 30, 60, 90];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const totalRecords = (counts) =>
  !counts ? 0 : Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0);

// Save a JSON object to the browser as a downloaded file.
function downloadJson(payload, filename) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { URL.revokeObjectURL(url); } catch { /* noop */ }
    try { document.body.removeChild(a); } catch { /* noop */ }
  }, 800);
}

export default function BackupTab() {
  const { user } = useAuth();
  const isAdmin = !!user && (user.is_company_admin || user.role === "admin");

  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);

  const [autoBackup, setAutoBackup] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const fileRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    try {
      setLoading(true);
      const [listRes, cfgRes] = await Promise.all([
        backupsApi.list(),
        autoBackupApi.get().catch(() => ({ data: null })),
      ]);
      setBackups(listRes.data || []);
      if (cfgRes?.data) setAutoBackup(cfgRes.data);
    } catch (e) {
      toast.error(apiError(e, "Failed to load backups"));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      // Two-step: POST returns metadata + id, then GET pulls the full payload.
      const createRes = await backupsApi.create();
      const created = createRes.data || {};
      const bid = created.id || created.meta?.backup_id;
      if (!bid) throw new Error("Backup created but no id returned.");
      const full = (await backupsApi.getOne(bid)).data || {};
      const meta = full.meta || created.meta || {};
      const data = full.data || {};
      const total = totalRecords(meta?.counts || created?.meta?.counts);
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadJson({ meta, data }, `fdcheck-backup-${stamp}-${total}rec.json`);
      await refresh();
      toast.success(`Backup created — ${total.toLocaleString()} records saved to your device and the server.`);
    } catch (e) {
      toast.error(apiError(e, "Failed to create backup"));
    } finally {
      setCreating(false);
    }
  };

  const handleRedownload = async (b) => {
    if (busyId) return;
    setBusyId(b.id);
    try {
      const data = (await backupsApi.getOne(b.id)).data || {};
      const total = totalRecords(b.counts);
      const stamp = b.created_at.replace(/[:.]/g, "-").slice(0, 19);
      downloadJson({ meta: data.meta, data: data.data }, `fdcheck-backup-${stamp}-${total}rec.json`);
    } catch (e) {
      toast.error(apiError(e, "Failed to download"));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (b) => {
    const total = totalRecords(b.counts);
    if (!window.confirm(`Permanently delete the snapshot from ${formatDate(b.created_at)} (${total.toLocaleString()} records)? This cannot be undone.`)) return;
    setBusyId(b.id);
    try {
      await backupsApi.remove(b.id);
      await refresh();
      toast.success("Backup deleted");
    } catch (e) {
      toast.error(apiError(e, "Failed to delete"));
    } finally {
      setBusyId(null);
    }
  };

  const handlePickFile = () => {
    if (restoring) return;
    fileRef.current?.click();
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      toast.error("File is not a valid FD Check backup JSON.");
      return;
    }
    const total = totalRecords(parsed?.meta?.counts);
    if (!window.confirm(`Restore this backup? ${total.toLocaleString()} records will be MERGED into your live data — existing records are never overwritten, only missing items are added.`)) return;
    setRestoring(true);
    try {
      const res = await backupsApi.restore(parsed);
      setRestoreResult(res.data?.summary || {});
      await refresh();
    } catch (err) {
      toast.error(apiError(err, "Failed to restore"));
    } finally {
      setRestoring(false);
    }
  };

  const handleRestoreFromHistory = async (b) => {
    const total = totalRecords(b.counts);
    if (!window.confirm(`Restore the snapshot from ${formatDate(b.created_at)} (${total.toLocaleString()} records)? Records are MERGED — existing data is never overwritten.`)) return;
    setBusyId(b.id);
    try {
      const full = (await backupsApi.getOne(b.id)).data || {};
      const res = await backupsApi.restore({ meta: full.meta, data: full.data });
      setRestoreResult(res.data?.summary || {});
      await refresh();
    } catch (e) {
      toast.error(apiError(e, "Failed to restore"));
    } finally {
      setBusyId(null);
    }
  };

  const patchAuto = async (patch) => {
    if (autoSaving) return;
    setAutoSaving(true);
    try {
      const res = await autoBackupApi.update(patch);
      setAutoBackup(res.data);
    } catch (e) {
      toast.error(apiError(e, "Failed to update schedule"));
    } finally {
      setAutoSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (runningNow) return;
    setRunningNow(true);
    try {
      const res = await autoBackupApi.runNow();
      if (res.data?.ok) {
        toast.success(`Scheduled backup ran — ${totalRecords(res.data?.counts).toLocaleString()} records snapshotted.`);
      } else {
        toast.error(res.data?.error || "Backup failed");
      }
      await refresh();
    } catch (e) {
      toast.error(apiError(e, "Failed to run backup"));
    } finally {
      setRunningNow(false);
    }
  };

  if (!isAdmin) {
    return (
      <Card className="mx-auto max-w-lg space-y-3 p-8 text-center">
        <Lock className="mx-auto h-10 w-10 text-slate-400" />
        <p className="font-semibold text-slate-700">Backup &amp; restore is admin-only.</p>
        <p className="text-sm text-slate-500">Ask a company admin to manage data backups.</p>
      </Card>
    );
  }

  return (
    <div className="max-w-3xl space-y-5">
      {/* Intro */}
      <Card className="flex items-start gap-3 border-emerald-200 bg-emerald-50 p-4">
        <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
        <div>
          <p className="font-semibold text-emerald-800">Safety net for your data</p>
          <p className="text-sm text-emerald-700">
            Take a full snapshot of every building, door, inspection, quote and invoice.
            The file downloads to this device AND is kept in a rolling server-side history
            (last 20 backups). Restore is merge-only — it never overwrites current work.
          </p>
        </div>
      </Card>

      {/* Primary actions */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={handleCreate} disabled={creating} data-testid="backup-create" className="flex-1 min-w-[180px]">
          {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CloudDownload className="mr-2 h-4 w-4" />}
          Create backup now
        </Button>
        <Button onClick={handlePickFile} disabled={restoring} variant="outline" data-testid="backup-restore-file" className="flex-1 min-w-[180px]">
          {restoring ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CloudUpload className="mr-2 h-4 w-4" />}
          Restore from file
        </Button>
        <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={handleFileChosen} data-testid="backup-file-input" />
      </div>

      {/* Nightly auto-backup */}
      <Card className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-semibold text-slate-800">
              Nightly auto-backup · <span className={autoBackup?.enabled ? "text-emerald-600" : "text-slate-400"}>{autoBackup?.enabled ? "Enabled" : "Disabled"}</span>
            </p>
            <p className="text-sm text-slate-500">
              {autoBackup?.enabled
                ? `Runs every night at ${String(autoBackup.hour_utc ?? 2).padStart(2, "0")}:00 UTC · keeps last ${autoBackup.retention ?? 30} snapshots.`
                : "Turn on to snapshot your data automatically every night. Nothing is deleted from your live data."}
            </p>
            {autoBackup?.last_run_at && (
              <p className={`mt-1 text-sm ${autoBackup.last_status === "error" ? "text-red-600" : "text-emerald-700"}`}>
                Last run: {formatDate(autoBackup.last_run_at)}{autoBackup.last_status === "error" ? ` · ${autoBackup.last_error || "failed"}` : " · OK"}
              </p>
            )}
          </div>
          <Switch
            checked={!!autoBackup?.enabled}
            disabled={autoSaving}
            onCheckedChange={(v) => patchAuto({ enabled: v })}
            data-testid="auto-backup-toggle"
          />
        </div>

        {autoBackup?.enabled && (
          <div className="space-y-4 border-t border-slate-100 pt-4">
            <div>
              <p className="mb-1.5 text-sm font-medium text-slate-600">Run at (UTC)</p>
              <div className="flex flex-wrap gap-2">
                {HOUR_OPTIONS.map((h) => (
                  <button
                    key={h}
                    disabled={autoSaving}
                    onClick={() => patchAuto({ hour_utc: h })}
                    data-testid={`auto-backup-hour-${h}`}
                    className={`rounded-full border px-3 py-1 text-sm font-medium transition ${
                      autoBackup.hour_utc === h
                        ? "border-emerald-500 bg-emerald-500 text-white"
                        : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {String(h).padStart(2, "0")}:00
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium text-slate-600">Keep last</p>
              <div className="flex flex-wrap gap-2">
                {RETENTION_OPTIONS.map((n) => (
                  <button
                    key={n}
                    disabled={autoSaving}
                    onClick={() => patchAuto({ retention: n })}
                    data-testid={`auto-backup-retention-${n}`}
                    className={`rounded-full border px-3 py-1 text-sm font-medium transition ${
                      autoBackup.retention === n
                        ? "border-emerald-500 bg-emerald-500 text-white"
                        : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {n} snapshots
                  </button>
                ))}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleRunNow} disabled={runningNow} data-testid="auto-backup-run-now">
              {runningNow ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Run the schedule now (test)
            </Button>
          </div>
        )}
      </Card>

      {/* Server-side history */}
      <div>
        <p className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">Server-side history</p>
        {loading ? (
          <Card className="flex items-center justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></Card>
        ) : backups.length === 0 ? (
          <Card className="flex flex-col items-center gap-2 p-8 text-center">
            <Archive className="h-9 w-9 text-slate-300" />
            <p className="font-semibold text-slate-600">No backups yet.</p>
            <p className="text-sm text-slate-400">Click “Create backup now” to take your first snapshot.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {backups.map((b) => {
              const total = totalRecords(b.counts);
              const isBusy = busyId === b.id;
              return (
                <Card key={b.id} className="space-y-3 p-4" data-testid={`backup-row-${b.id}`}>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-indigo-500" />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{formatDate(b.created_at)}</p>
                      <p className="text-sm text-slate-500">{total.toLocaleString()} records · by {b.created_by_name || "admin"}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(b.counts || {}).slice(0, 6).map(([k, v]) => (
                      <span key={k} className="rounded border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                        {COLLECTION_LABELS[k] || k} · {v}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleRedownload(b)} disabled={isBusy} data-testid={`backup-download-${b.id}`}>
                      <Download className="mr-1.5 h-4 w-4" /> Download
                    </Button>
                    <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => handleRestoreFromHistory(b)} disabled={isBusy} data-testid={`backup-restore-${b.id}`}>
                      <RefreshCw className="mr-1.5 h-4 w-4" /> Restore
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(b)} disabled={isBusy} data-testid={`backup-delete-${b.id}`}>
                      <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                    </Button>
                    {isBusy && <Loader2 className="h-4 w-4 animate-spin self-center text-primary" />}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Restore result dialog */}
      <Dialog open={!!restoreResult} onOpenChange={(o) => !o && setRestoreResult(null)}>
        <DialogContent data-testid="backup-restore-result">
          <DialogHeader>
            <DialogTitle>Restore complete</DialogTitle>
            <DialogDescription>Records added to your live data — existing items were never overwritten.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {Object.entries(restoreResult || {}).map(([coll, s]) => (
              <div key={coll} className="flex items-center justify-between border-b border-slate-50 py-1.5 text-sm">
                <span className="font-medium text-slate-800">{COLLECTION_LABELS[coll] || coll}</span>
                <span className="text-slate-500">+{s.added} added · {s.skipped} skipped · {s.errors} errors</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setRestoreResult(null)} data-testid="backup-restore-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
